import { format } from "date-fns";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

// ─── Core helper ─────────────────────────────────────────────────────────────

/**
 * Ensure we always have a real Date object.
 * Handles the case where the API returns a plain string instead of a Date.
 */
const toDate = (date: Date | string): Date => {
    if (date instanceof Date) return date;
    // If the string has no timezone suffix, assume UTC (add Z)
    const s = date as string;
    return new Date(/Z$|[+-]\d{2}:\d{2}$/.test(s) ? s : s + "Z");
};

export class DateUtility {

    public static getDate = (date: Date): Date => {
        try { date.getTime(); return date; } catch { return new Date(date); }
    }

    // ── Relative time ─────────────────────────────────────────────────────────

    public static timeAgo(date: Date | string): string {
        const now = new Date();
        const givenDate = toDate(date);
        const diffInSeconds = Math.floor((now.getTime() - givenDate.getTime()) / 1000);

        if (diffInSeconds < 60)        return `${diffInSeconds} second${diffInSeconds !== 1 ? "s" : ""} ago`;
        if (diffInSeconds < 3600)      { const m = Math.floor(diffInSeconds / 60);     return `${m} minute${m !== 1 ? "s" : ""} ago`; }
        if (diffInSeconds < 86400)     { const h = Math.floor(diffInSeconds / 3600);   return `${h} hour${h !== 1 ? "s" : ""} ago`; }
        if (diffInSeconds < 604800)    { const d = Math.floor(diffInSeconds / 86400);  return `${d} day${d !== 1 ? "s" : ""} ago`; }
        if (diffInSeconds < 2592000)   { const w = Math.floor(diffInSeconds / 604800); return `${w} week${w !== 1 ? "s" : ""} ago`; }
        if (diffInSeconds < 31536000)  { const mo = Math.floor(diffInSeconds / 2592000); return `${mo} month${mo !== 1 ? "s" : ""} ago`; }
        const y = Math.floor(diffInSeconds / 31536000);
        return `${y} year${y !== 1 ? "s" : ""} ago`;
    }

    // ── Format (UTC → browser local timezone) ─────────────────────────────────

    /**
     * Format a date for display.
     * Dates from the API are UTC ("Z" suffix). JS Date automatically converts
     * UTC to the browser's local timezone when you call format/getHours etc.
     */
    public static Format = (dateReceived: Date | string | undefined | null, dateFormat: string): string => {
        if (!dateReceived) return "--";
        try {
            const d = toDate(dateReceived as Date | string);
            if (isNaN(d.getTime())) return "--";
            return format(d, dateFormat);
        } catch {
            return "--";
        }
    }

    // ── Parse (for display blocks) ────────────────────────────────────────────
    public static TimeLocal_text = (date1: Date | string):string  => {
        const date = this.ParseDate(date1 as Date | string);
        return date.time;
    }

    public static ParseDate = (date1: Date | string): { day: number; month: string; year: number; weekday: string; time: string } => {  
        const date = toDate(date1 as Date | string);
 
        const formatted = date.toLocaleDateString("en-US", {
            weekday: "long", month: "long", day: "numeric",
            year: "numeric", hour: "numeric", minute: "numeric",
        });
        const parts = formatted.split(", ");
        const weekday = parts[0];
        const [month, dayStr] = parts[1].split(" ");
        const day  = parseInt(dayStr);
        const year = parseInt(parts[2]);
        const time =  (formatted.indexOf(' at ')>1?
                        formatted.split(' at ')[1]:
                        parts[3] )?? ""; 
        return { day, month, year, weekday, time };
    }

    // ── Duration ──────────────────────────────────────────────────────────────

    public static Duration = (startDate: Date | string, endDate: Date | string): { hours: number; minutes: number } => {
        const diff = Math.abs(toDate(endDate as Date | string).getTime() - toDate(startDate as Date | string).getTime());
        const hours   = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        return { hours, minutes };
    }

    public static DurationText = (startDate: Date | string, endDate?: Date | string): string => {
        const end = endDate ?? new Date();
        const d = DateUtility.Duration(startDate, end);
        return `${d.hours}h ${d.minutes}min`;
    }

    public static getDateDiffInDays(date1: Date | string, date2: Date | string): number {
        return Math.ceil(
            Math.abs(toDate(date2 as Date | string).getTime() - toDate(date1 as Date | string).getTime())
            / (1000 * 60 * 60 * 24)
        );
    }

    /**
     * Returns the browser's local timezone name, e.g. "America/New_York"
     */
    public static localTimezone = (): string =>
        Intl.DateTimeFormat().resolvedOptions().timeZone;
}

// ─── UTC ↔ Local helpers ──────────────────────────────────────────────────────

/**
 * Convert a UTC date (from API) → local Date for display/input in date pickers.
 * JS Date objects are always UTC internally; the picker displays local time.
 */
export function utcToLocal(utcDate: Date | string | null | undefined): Date | undefined {
    if (!utcDate) return undefined;
    const d = toDate(utcDate as Date | string);
    return isNaN(d.getTime()) ? undefined : d;
}

/**
 * Convert a local Date (from date picker) → UTC Date before sending to API.
 * JSON.stringify() calls .toISOString() which always produces a UTC "Z" string.
 */
export function localToUtc(localDate: Date | string | null | undefined): Date | undefined {
    if (!localDate) return undefined;
    const d = localDate instanceof Date ? localDate : new Date(localDate as string);
    return isNaN(d.getTime()) ? undefined : d;
}

// ─── Lecture date normalizers ─────────────────────────────────────────────────

export function normalizeLectureDatesToLocal<T extends {
    startDate?:   Date | string | null;
    endDate?:     Date | string | null;
    sessionDate?: Date | string | null;
}>(req: T): Omit<T, "startDate" | "endDate" | "sessionDate"> & {
    startDate?:   Date;
    endDate?:     Date;
    sessionDate?: Date;
} {
    return { ...req, startDate: utcToLocal(req.startDate), endDate: utcToLocal(req.endDate), sessionDate: utcToLocal(req.sessionDate) };
}

export function normalizeLectureDatesToUtc<T extends {
    startDate?:   Date | string | null;
    endDate?:     Date | string | null;
    sessionDate?: Date | string | null;
}>(req: T): Omit<T, "startDate" | "endDate" | "sessionDate"> & {
    startDate?:   Date;
    endDate?:     Date;
    sessionDate?: Date;
} {
    return { ...req, startDate: localToUtc(req.startDate), endDate: localToUtc(req.endDate), sessionDate: localToUtc(req.sessionDate) };
}

// ─── Validation helpers ───────────────────────────────────────────────────────

export const validateDate = (
    date: Date | undefined | null,
    fieldName: string,
    minDate: Dayjs | null | undefined,
    minDateErrMsg: string | null | undefined,
    maxDate: Dayjs | null | undefined,
    maxDateErrMsg: string | null | undefined,
): any => {
    if (!date) return true;
    const f = dayjs(date);
    if (!f.isValid()) return `Invalid Date. Please enter a valid ${fieldName}.`;
    if (minDate && f.isBefore(minDate, "day")) return minDateErrMsg;
    if (maxDate && f.isAfter(maxDate, "day"))  return maxDateErrMsg;
    return true;
};

export const validateDate2 = (
    date: Date | undefined | null,
    fieldName: string,
    dateMin: Date | null | undefined,
    minDateErrMsg: string | null | undefined,
    dateMax: Date | null | undefined,
    maxDateErrMsg: string | null | undefined,
): any => {
    if (!date) return true;
    const f = dayjs(date);
    if (!f.isValid()) return `Invalid Date. Please enter a valid ${fieldName}.`;
    if (dateMin && f.isBefore(dayjs(dateMin), "day")) return minDateErrMsg;
    if (dateMax && f.isAfter(dayjs(dateMax),  "day")) return maxDateErrMsg;
    return true;
};

export const combineTimeWithDate = (time: string, date: Date): Date => {
    const [hours, minutes] = time.split(":").map(Number);
    const combined = new Date(date);
    combined.setHours(hours, minutes, 0, 0);
    return combined;
};

// ─── Legacy ───────────────────────────────────────────────────────────────────

/** @deprecated use DateUtility.Format instead */
export const prettifiedPstString = (date: Date | string): string => {
    if (!date) return "";
    return toDate(date as Date | string).toLocaleString();
};
